/**
 * CLOUDWEB-21024: кэш Omicron-конфига в Service Worker.
 *
 * SW не может читать фичи сам: `Cloud/Application/FeaturesEs6` берёт их из
 * `window.cloudSettings`, а `window` в SW нет — там `getFeature()` всегда falsy. Поэтому
 * конфиг приезжает со страницы по postMessage (контракт — `serviceWorker/omicronConfigBridge`).
 *
 * Одного postMessage недостаточно: SW выгружается на idle и может быть поднят пушем, когда
 * ни одной вкладки нет и прислать конфиг некому. Поэтому последнее полученное значение
 * лежит в IDB и читается на старте SW.
 *
 * Контракт как у `claimPushId`: наружу не всплывает ни одна ошибка IDB. Нет конфига или
 * IDB недоступен — `null`, то есть все зависящие от фич ветки ведут себя как раньше.
 */
import type { SwOmicronConfig } from 'serviceWorker/omicronConfigBridge';
// SW_CONFIG_DB — отдельная база, а не второй store в `cloud-sw`: bump версии `cloud-sw` до 2
// затронул бы путь дедупа пушей (и мог бы зависнуть на onblocked, пока старый SW держит соединение).
import { ensureStore, SW_CONFIG_DB } from 'serviceWorker/swIdbSchema';

/** Ключ единственного рекорда: конфиг один на origin, версионировать его нечем. */
const KEY = 'omicron';

interface ConfigRecord {
    config: SwOmicronConfig;
    /** Когда снимок записан в IDB — видно, насколько конфиг в SW отстал от страницы. */
    savedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

async function openDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') {
        return null;
    }

    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = indexedDB.open(SW_CONFIG_DB.name, SW_CONFIG_DB.version);

            request.onupgradeneeded = () => {
                ensureStore(request.result, SW_CONFIG_DB);
            };

            request.onsuccess = () => {
                const db = request.result;

                // Не держим соединение, если схему апгрейдит другой контекст: иначе его open зависнет на onblocked. Кэш сбрасываем — переоткроемся при следующем обращении.
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };

                resolve(db);
            };
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });

    return dbPromise;
}

/** Последнее известное значение: пришедшее из postMessage либо прочитанное из IDB. */
let memory: SwOmicronConfig | null = null;
/** Метка записи снимка, который лежит в `memory` — отдаём в диагностику как есть. */
let memorySavedAt: number | null = null;
/** JSON-снимок того, что уже лежит в IDB — чтобы не писать одно и то же на каждую загрузку страницы. */
let savedSnapshot: string | null = null;
let readPromise: Promise<SwOmicronConfig | null> | null = null;

function snapshot(config: SwOmicronConfig): string | null {
    try {
        return JSON.stringify(config);
    } catch {
        return null;
    }
}

async function readFromDb(): Promise<ConfigRecord | null> {
    try {
        const db = await openDb();

        if (!db) {
            return null;
        }

        return await new Promise<ConfigRecord | null>((resolve) => {
            try {
                const request = db.transaction(SW_CONFIG_DB.store, 'readonly').objectStore(SW_CONFIG_DB.store).get(KEY);

                request.onsuccess = () => {
                    const record = request.result as ConfigRecord | undefined;

                    resolve(record?.config ? record : null);
                };
                request.onerror = () => resolve(null);
            } catch {
                resolve(null);
            }
        });
    } catch {
        return null;
    }
}

/** Однократное чтение IDB с прогревом памяти. Вынесено, чтобы `getSwOmicronConfig` мемоизировал промис. */
async function readAndCacheConfig(): Promise<SwOmicronConfig | null> {
    const record = await readFromDb();

    // postMessage мог опередить чтение — значение из памяти свежее дискового.
    if (memory) {
        return memory;
    }

    if (record) {
        memory = record.config;
        memorySavedAt = record.savedAt ?? null;
        savedSnapshot = snapshot(record.config);
    }

    return memory;
}

/**
 * Актуальный конфиг: из памяти, иначе однократное чтение IDB (результат кэшируется).
 * `null` — конфига ещё не было либо IDB недоступен.
 */
export function getSwOmicronConfig(): Promise<SwOmicronConfig | null> {
    if (memory) {
        return Promise.resolve(memory);
    }

    // Функция намеренно не async: мемоизируем сам промис, чтобы параллельные вызовы делили
    // одно чтение IDB. Async-обёртка возвращала бы новый промис на каждый вызов.
    if (!readPromise) {
        readPromise = readAndCacheConfig();
    }

    return readPromise;
}

/** Принять конфиг со страницы: сразу в память, затем (если изменился) в IDB. Никогда не бросает. */
export async function setSwOmicronConfig(config: SwOmicronConfig): Promise<void> {
    // Синхронно и ДО первого await: следующий же swDebugLog должен видеть новое значение,
    // не дожидаясь IDB.
    memory = config;

    const next = snapshot(config);

    if (next !== null && next === savedSnapshot) {
        return;
    }

    try {
        const db = await openDb();

        if (!db) {
            return;
        }

        await new Promise<void>((resolve) => {
            try {
                const record: ConfigRecord = { config, savedAt: Date.now() };
                const tx = db.transaction(SW_CONFIG_DB.store, 'readwrite');

                tx.objectStore(SW_CONFIG_DB.store).put(record, KEY);

                tx.oncomplete = () => {
                    savedSnapshot = next;
                    memorySavedAt = record.savedAt;
                    resolve();
                };
                tx.onerror = () => resolve();
                tx.onabort = () => resolve();
            } catch {
                resolve();
            }
        });
    } catch {
        // Контракт модуля: наружу не всплывает ни одна ошибка IDB.
    }
}

/**
 * Включён ли дедуп показанных пушей (`webpush-config.enablePushDedup`).
 *
 * Fail-safe в сторону дедупа: выключаем только явным `false` из Omicron. Отсутствие поля,
 * отсутствие конфига в кэше (SW поднят пушем до первого визита) и недоступный IDB — всё это
 * даёт `true`, потому что цена ошибки асимметрична: без дедупа пользователь видит один пуш
 * дважды, а лишний дедуп максимум подавит настоящий повтор в пределах TTL.
 */
export async function isPushDedupEnabled(): Promise<boolean> {
    const config = await getSwOmicronConfig();

    return config?.webpushConfig?.enablePushDedup !== false;
}

/**
 * Состояние кэша для диагностики (`status().swConfig.live` со страницы и `app.webPush.config()`
 * в консоли воркера): то, чем воркер реально пользуется, вместе с меткой записи.
 * `savedAt === null` при конфиге из памяти, который ещё не долетел до IDB, — расхождение
 * с диском видно сразу.
 */
export async function getSwOmicronConfigState(): Promise<{ config: SwOmicronConfig | null; savedAt: number | null }> {
    const config = await getSwOmicronConfig();

    return { config, savedAt: memorySavedAt };
}

/** Всё, что нужно правилам подавления пуша, одним чтением конфига. */
export interface SuppressionConfig {
    enableOpenAppSuppress: boolean;
    presenceTtlMs: number;
    skipDeeplinkPushOnDesktop: boolean;
    skipDeeplinkPushWithoutTarget: boolean;
    deeplinkTarget?: boolean;
}

/**
 * Срок жизни метки маяка по умолчанию.
 *
 * Втрое больше интервала записи со страницы (5 с) — переживает два потерянных тика. Верхняя
 * граница задана не точностью, а ценой ошибки: всё это время после закрытия приложения пуши
 * подавлялись бы, если бы страница не помечала метку протухшей при уходе в фон.
 */
const DEFAULT_PRESENCE_TTL_MS = 15000;

/**
 * Параметры подавления с fail-safe дефолтами.
 *
 * Направление дефолтов везде одно: нет конфига — показываем пуш. Отсутствие снимка означает,
 * что воркер подняли пушем до первого визита на страницу, и принимать в этот момент решение
 * «подавить» не на чем. Исключение — `skipDeeplinkPushOnDesktop`: он выключается только явным
 * `false`, потому что на десктопе диплинк открыть нечем в принципе, и цена ошибки там нулевая.
 *
 * `deeplinkTarget` остаётся `undefined` при отсутствии признака и не подменяется на `false`:
 * различие важно для читателя, а решение «подавлять ли отсутствие признака» принимает рубильник.
 */
export async function getSuppressionConfig(): Promise<SuppressionConfig> {
    const config = await getSwOmicronConfig();
    const webpush = config?.webpushConfig;

    return {
        enableOpenAppSuppress: webpush?.enableOpenAppSuppress === true,
        presenceTtlMs: typeof webpush?.presenceTtlMs === 'number' ? webpush.presenceTtlMs : DEFAULT_PRESENCE_TTL_MS,
        skipDeeplinkPushOnDesktop: webpush?.skipDeeplinkPushOnDesktop !== false,
        skipDeeplinkPushWithoutTarget: webpush?.skipDeeplinkPushWithoutTarget === true,
        deeplinkTarget: config?.deeplinkTarget,
    };
}
